// React.
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTable, useSortBy, usePagination, Column } from 'react-table';

// Third-parties.
import * as dayjs from 'dayjs';
import TableCSVExport from '../../../../component/TableCSVExport';

// Types.
import InstanceTarget from '../../../../types/instance/InstanceTarget';

// Helper.
import {
    getDefaultPageSize,
    getTimeZone
} from '../../../../helpers/utils';
import api from "../../../../api/Base";
import { TimeRangeContext } from "../../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../../helpers/timeRangeUtils";
import Helper from "../../../../helpers/Helper";

// Components.
import ReactTable from '../../../../component/ReactTable';
import { useNavigate, useLocation } from "react-router-dom";
import AgentServerPanel from "./AgentServerPanel";
import FilterButtonGroup from '../../../../component/FilterButtonGroup';
import Alert from '../../../../component/Alert';
import SearchInput from '../../../../component/SearchInput';

export default function JobExecutions(props: {
    instance: InstanceTarget,
    showByDefault: boolean,
    jobNameFilter?: string
}) {
    const { instance, showByDefault, jobNameFilter } = props;
    const timeRangeContext = useContext(TimeRangeContext);

    const [loading, setLoading] = useState<boolean>(true);
    const [jobs, setJobs] = useState<any[]>([]);

    // Table related state
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const [statusFilter, setStatusFilter] = useState(query.get("statusFilter") || "all");
    const inputRef = useRef<HTMLInputElement>(null);

    // State for details panel
    const [selectedServer, setSelectedServer] = useState<any>(null);
    const [panelOpen, setPanelOpen] = useState(false);

    useEffect(() => {
        if (inputRef.current && showByDefault) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, [showByDefault]);

    void useMemo(async () => {
        try {
            setLoading(true);
            const jobNameParam = jobNameFilter ? `&jobname=${encodeURIComponent(jobNameFilter)}` : '';
            const url = `job/history?${buildApiQuery(timeRangeContext.timeRange)}&id=${instance.id}${jobNameParam}`;
            const jobsResponse = await api.get(url);

            let filteredData = Array.isArray(jobsResponse.data) ? jobsResponse.data : [];

            setJobs(filteredData);
            setLoading(false);
        } catch (error) {
            console.error('Failed to retrieve job history data:', error);
            setJobs([]);
            setLoading(false);
        }
    }, [timeRangeContext, instance.id, instance.type, jobNameFilter]);

    useEffect(() => {
        if (selectedServer && jobs.length > 0) {
            const updatedJob = jobs.find(job => job.jobid === selectedServer.jobid && job.timeslice === selectedServer.timeslice);
            if (updatedJob) {
                setSelectedServer(updatedJob);
            } else {
                setSelectedServer(null);
                setPanelOpen(false);
            }
        }
    }, [jobs]);

    const filteredJobs = useMemo(() =>
        jobs.filter((job) => {
            const jobName = job.jobname || 'N/A';
            const serverName = job.server || 'N/A';
            const jobCategory = job.jobcategory || 'N/A';
            const statusText = (job.statustext || '').toLowerCase();
            
            const matchesTextFilter = jobName.toLowerCase().includes(tableFilter.toLowerCase()) ||
                   serverName.toLowerCase().includes(tableFilter.toLowerCase()) ||
                   jobCategory.toLowerCase().includes(tableFilter.toLowerCase()) ||
                   statusText.includes(tableFilter.toLowerCase());
            
            // Apply status filter
            const matchesStatusFilter = statusFilter === 'all' ||
                (statusFilter === 'succeeded' && statusText.includes('succeeded')) ||
                (statusFilter === 'failed' && statusText.includes('failed'));
            
            return matchesTextFilter && matchesStatusFilter;
        }).map((job, index) => ({
            ...job,
            _rowIndex: index
        })),
        [jobs, tableFilter, statusFilter]);


    const columns: Column[] = useMemo(() => [
        {
            Header: 'Result',
            accessor: 'statustext',
            className: 'text-center',
            headerClassName: 'text-center',
            canSort: true,
            Cell: ({ value }: { value: string }) => {
                const isSuccess = value?.toLowerCase().includes('succeed');
                const isFailed = value?.toLowerCase().includes('fail');
                return (
                        <span className={isSuccess ? 'text-success' : isFailed ? 'text-danger' : ''}>
                        {isSuccess && <i className="fal fa-check-circle fa-fw"/>}
                            {isFailed && <i className="fal fa-times-circle fa-fw"/>}
                    </span>
                );
            }
        },
        {
            Header: 'Start Time',
            accessor: 'timeslice',
            canSort: true,
            Cell: ({ value }: { value: string }) => {
                if (!value) return 'N/A';
                const tz = getTimeZone();
                const isUTC = tz === 'UTC';
                const time = (isUTC ? dayjs.utc(value) : dayjs.default(value)).tz(tz);
                return time.format('YYYY-MM-DD HH:mm:ss');
            }
        },
        {
            Header: 'Job Name',
            accessor: 'jobname',
            canSort: true,
            Cell: ({ row, value }: { row: any, value: string }) => (
                    <span className='panel-link' onClick={() => openPanel(row.original)}>
                    {value || 'N/A'}
                </span>
            )
        },
        {
            Header: 'Message',
            accessor: 'message',
            canSort: true,
        },
        {
            Header: 'Agent Server Name',
            accessor: 'server',
            canSort: true
        },
        {
            Header: 'Duration',
            accessor: 'duration',
            className: 'text-end',
            headerClassName: 'text-end',
            canSort: true,
            Cell: ({cell}: any) => Number(cell.row.values.duration) === 0 ? '0' : (Helper.getTimeInEnglish(cell.row.values.duration))
        }
    ], []);

    const tableInstance = useTable(
            {
                columns,
                data: filteredJobs,
            initialState: {
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
                sortBy: [{ id: 'timeslice', desc: true }]
            }
            },
            useSortBy,
            usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        if (statusFilter !== 'all') {
            params.set('statusFilter', statusFilter);
        } else {
            params.delete('statusFilter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());
        navigate({ search: params.toString() }, { replace: true });
    }, [pageIndex, pageSize, navigate, location.search, tableFilter, statusFilter]);

    useEffect(() => {
        if (tableFilter || statusFilter !== 'all') {
            gotoPage(0);
        }
    }, [tableFilter, statusFilter, gotoPage]);

    const openPanel = (job: any) => {
        setSelectedServer(job);
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setSelectedServer(null);
    };

    const getRowClassName = (selectedItem: any) => {
        const className = "table-row";
        const isSameServer = selectedServer &&
            selectedItem._rowIndex === selectedServer._rowIndex;

        return isSameServer ? `${className} selected` : className;
    };

    function clearTableFilter() {
        setTableFilter('');
        setStatusFilter('all');
    }

    return (
        <div id="job-executions" className={`tab-pane fade ${showByDefault ? 'show active' : ''}`} role="tabpanel" aria-labelledby="job-executions-tab">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-history fa-fw" aria-hidden="true"/>
                    Job Executions
                    <span className="badge bg-info"
                          data-tooltip-id="global-tooltip" data-tooltip-content="Filtered job execution count">{filteredJobs.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapseJobExecutions" aria-expanded="false" aria-controls="collapseJobExecutions"/>
                </div>
                <div id="collapseJobExecutions" className="card-body collapse show">
                    {Number(process.env.REACT_APP_API_LIMIT) - 1 === jobs.length - 1 && (
                        <Alert
                            message={`To help preserve performance, we limit the total number of job execution records in the table below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} ordered by start time. The summary widgets above show totals for all job executions in the selected time period, not just the visible records.`}
                            heading="Job execution records" variant="alert-info"/>
                    )}
                    <div className="row table-search align-items-center">
                        <div className="col-auto">
                        <FilterButtonGroup
                            options={[
                                { label: 'All', value: 'all', variant: 'primary' },
                                { label: 'Succeeded', value: 'succeeded', variant: 'success', icon: 'fa-check-circle' },
                                { label: 'Failed', value: 'failed', variant: 'danger', icon: 'fa-times-circle' }
                            ]}
                            selectedValue={statusFilter}
                            onChange={setStatusFilter}
                            ariaLabel="Status filter"
                        />
                        </div>
                        <div className="col-auto ms-auto">
                            <TableCSVExport
                                tableInstance={tableInstance}
                                filename="SQL Agent Jobs"
                            />
                        </div>
                        <div className="col-md-2">
                            <SearchInput
                                ref={inputRef}
                                value={tableFilter}
                                onChange={(e) => setTableFilter(e.target.value)}
                                onClear={clearTableFilter}
                            />
                        </div>
                    </div>
                    <ReactTable
                        getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        nextPage={nextPage}
                        previousPage={previousPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        dataLength={filteredJobs.length}
                        error={null}
                        loading={loading}
                        getRowProps={(row) => ({ className: getRowClassName(row.original) })}
                    />
                </div>
            </div>
            
            {/* Slide-out panel for agent server details */}
            <AgentServerPanel
                server={selectedServer}
                open={panelOpen}
                onClose={closePanel}
                instanceType={instance.type}
                key={selectedServer?.jobid + '-' + selectedServer?.timeslice || 'none'}
            />
        </div>
    );
}

